export const cabinetSystemIds = ['pcs-200', 'fcs-200', 'g3-dm2'] as const
export type CabinetSystemId = (typeof cabinetSystemIds)[number]

export interface CabinetSystemProfile {
  id: CabinetSystemId
  displayName: string
  aliases: string[]
  controllerLabel: string
  contentEntityId: string
  recommendedMaxPoints: number | null
}

export const cabinetSystems: readonly CabinetSystemProfile[] = [
  {
    id: 'pcs-200',
    displayName: 'PCS-200',
    aliases: ['PSC200', 'OptiSYS PCS-200'],
    controllerLabel: 'PCS-200 CPU + IO',
    contentEntityId: 'pcs-200-controller-family',
    recommendedMaxPoints: 100
  },
  {
    id: 'fcs-200',
    displayName: 'FCS200',
    aliases: ['FSC200'],
    controllerLabel: 'FCS200 / CS201 + WIO',
    contentEntityId: 'fcs-200-controller-family',
    recommendedMaxPoints: null
  },
  {
    id: 'g3-dm2',
    displayName: 'G3Smart / DM2',
    aliases: ['G3SDM2'],
    controllerLabel: 'G3Smart / DM2 CPU + IO',
    contentEntityId: 'g3-dm2-controller-family',
    recommendedMaxPoints: null
  }
]

export const cabinetProfileIds = [
  'cabinet-500x600',
  'cabinet-600x800',
  'cabinet-800x1000',
  'cabinet-1000x1200'
] as const

export type CabinetProfileId = (typeof cabinetProfileIds)[number]

export interface CabinetProfile {
  id: CabinetProfileId
  title: string
  widthMm: number
  heightMm: number
  minPoints: number
  maxPoints: number | null
  pointRangeLabel: string
}

export const cabinetProfiles: readonly CabinetProfile[] = [
  {
    id: 'cabinet-500x600',
    title: '500 × 600 mm 控制柜',
    widthMm: 500,
    heightMm: 600,
    minPoints: 1,
    maxPoints: 50,
    pointRangeLabel: '1–50 点'
  },
  {
    id: 'cabinet-600x800',
    title: '600 × 800 mm 控制柜',
    widthMm: 600,
    heightMm: 800,
    minPoints: 51,
    maxPoints: 100,
    pointRangeLabel: '51–100 点'
  },
  {
    id: 'cabinet-800x1000',
    title: '800 × 1000 mm 控制柜',
    widthMm: 800,
    heightMm: 1000,
    minPoints: 101,
    maxPoints: 180,
    pointRangeLabel: '101–180 点'
  },
  {
    id: 'cabinet-1000x1200',
    title: '1000 × 1200 mm 控制柜',
    widthMm: 1000,
    heightMm: 1200,
    minPoints: 181,
    maxPoints: null,
    pointRangeLabel: '181 点及以上'
  }
]

export type CabinetLayoutItemKind =
  | 'protection'
  | 'power'
  | 'controller'
  | 'io'
  | 'terminal'
  | 'reserve'

export interface CabinetLayoutItem {
  id: string
  label: string
  kind: CabinetLayoutItemKind
  quantity: number
}

export interface CabinetLayoutRail {
  id: string
  label: string
  items: CabinetLayoutItem[]
}

export interface CabinetLayoutEstimate {
  pointCount: number
  pointBasis: string
  cabinet: CabinetProfile
  system: CabinetSystemProfile
  layoutTemplateId: string
  estimatedIoModules: number
  estimatedTerminalGroups: number
  reservePercent: number
  requiresEngineeringReview: boolean
  rails: CabinetLayoutRail[]
  warnings: string[]
}

export interface CabinetConfigurationSnapshot {
  kind: 'cabinet-layout'
  pointCount: number
  pointBasis: string
  systemId: CabinetSystemId
  systemLabel: string
  cabinetProfileId: CabinetProfileId
  cabinetTitle: string
  layoutTemplateId: string
  estimatedIoModules: number
  estimatedTerminalGroups: number
  reservePercent: number
  assumptions: string[]
}

function requirePointCount(pointCount: number) {
  if (!Number.isInteger(pointCount) || pointCount < 1 || pointCount > 5000) {
    throw new Error('点位数量必须是 1–5000 的整数')
  }
}

export function getCabinetSystem(systemId: CabinetSystemId): CabinetSystemProfile {
  const system = cabinetSystems.find((item) => item.id === systemId)
  if (system === undefined) throw new Error(`未知控制系统：${systemId}`)
  return system
}

export function recommendCabinet(pointCount: number): CabinetProfile {
  requirePointCount(pointCount)
  const profile = cabinetProfiles.find(
    (item) => item.maxPoints === null || pointCount <= item.maxPoints
  )
  if (profile === undefined) throw new Error('无法匹配机柜规格')
  return profile
}

export function estimateCabinetLayout(
  pointCount: number,
  systemId: CabinetSystemId
): CabinetLayoutEstimate {
  requirePointCount(pointCount)
  const cabinet = recommendCabinet(pointCount)
  const system = getCabinetSystem(systemId)
  const estimatedIoModules = Math.ceil(pointCount / 16)
  const estimatedTerminalGroups = Math.ceil(pointCount / 20)
  const reservePercent = 20
  const warnings = ['演示模拟，不构成设计、制造、接线、施工图或正式 BOM。']

  if (
    system.recommendedMaxPoints !== null &&
    pointCount > system.recommendedMaxPoints
  ) {
    warnings.push(
      `${system.displayName} 当前资料建议用于 ${system.recommendedMaxPoints} 点以内；本组合需复核多控制器或替代系统方案。`
    )
  }
  if (cabinet.maxPoints === null) {
    warnings.push('181 点及以上属于大型点位范围，柜体数量与散热必须由工程师复核。')
  }
  if (system.id === 'fcs-200') {
    warnings.push('FCS200 的 CS201/WIO 型号参数须以最新选型资料复核。')
  }

  return {
    pointCount,
    pointBasis: '物理 AI/AO/DI/DO 合计，不含 20% 布局预留',
    cabinet,
    system,
    layoutTemplateId: `${cabinet.id}-${system.id}`,
    estimatedIoModules,
    estimatedTerminalGroups,
    reservePercent,
    requiresEngineeringReview:
      cabinet.maxPoints === null ||
      (system.recommendedMaxPoints !== null &&
        pointCount > system.recommendedMaxPoints),
    rails: [
      {
        id: 'rail-protection',
        label: '保护与供电',
        items: [
          { id: 'main-protection', label: '总保护', kind: 'protection', quantity: 1 },
          { id: 'control-power', label: '控制电源', kind: 'power', quantity: 1 },
          { id: 'power-reserve', label: '电源预留', kind: 'reserve', quantity: 1 }
        ]
      },
      {
        id: 'rail-control',
        label: '控制与 IO',
        items: [
          {
            id: `${system.id}-controller`,
            label: system.controllerLabel,
            kind: 'controller',
            quantity: 1
          },
          {
            id: 'estimated-io',
            label: '模拟 IO 模块',
            kind: 'io',
            quantity: estimatedIoModules
          }
        ]
      },
      {
        id: 'rail-terminal',
        label: '现场端子',
        items: [
          {
            id: 'estimated-terminals',
            label: '模拟端子组',
            kind: 'terminal',
            quantity: estimatedTerminalGroups
          },
          {
            id: 'layout-reserve',
            label: `${reservePercent}% 布局预留`,
            kind: 'reserve',
            quantity: 1
          }
        ]
      }
    ],
    warnings
  }
}

export function toCabinetConfigurationSnapshot(
  estimate: CabinetLayoutEstimate
): CabinetConfigurationSnapshot {
  return {
    kind: 'cabinet-layout',
    pointCount: estimate.pointCount,
    pointBasis: estimate.pointBasis,
    systemId: estimate.system.id,
    systemLabel: estimate.system.displayName,
    cabinetProfileId: estimate.cabinet.id,
    cabinetTitle: estimate.cabinet.title,
    layoutTemplateId: estimate.layoutTemplateId,
    estimatedIoModules: estimate.estimatedIoModules,
    estimatedTerminalGroups: estimate.estimatedTerminalGroups,
    reservePercent: estimate.reservePercent,
    assumptions: [...estimate.warnings]
  }
}

